import JSONModel from "sap/ui/model/json/JSONModel";
import { RoleEnum } from "@ang/shared-types";
import { getLogger } from "../loggerService";

export type GuardConfig = {
	requiresAuth?: boolean;
	rolesAny?: RoleEnum[]; // at least one of
	rolesAll?: RoleEnum[]; // must include all
};

type AuthState = {
	isAuthenticated: boolean;
	role: RoleEnum | null; // single-role JWT now
	roles?: RoleEnum[]; // optional multi-role later
};

export class AuthGuardService {
	private logger = getLogger();

	constructor(private getAuthModel: () => JSONModel) {}

	private s(): AuthState {
		const m = this.getAuthModel();
		const data = m?.getData?.() as AuthState;
		return (
			data ?? {
				isAuthenticated: false,
				role: null,
				roles: [],
			}
		);
	}

	isAuthenticated(): boolean {
		return !!this.s().isAuthenticated;
	}

	private asSet(): Set<RoleEnum> {
		const { role, roles } = this.s();
		return new Set<RoleEnum>([...(roles ?? []), ...(role ? [role] : [])]);
	}

	check(cfg?: GuardConfig): boolean {
		this.logger.debug("AuthGuardService", "Auth guard check", cfg);

		if (!cfg) {
			this.logger.debug(
				"AuthGuardService",
				"No guard config - allowing access"
			);
			return true;
		}

		if (cfg.requiresAuth && !this.isAuthenticated()) {
			this.logger.warn(
				"AuthGuardService",
				"Authentication required but user not authenticated"
			);
			return false;
		}

		const have = this.asSet();
		this.logger.debug("AuthGuardService", "User roles", Array.from(have));
		this.logger.debug("AuthGuardService", "Auth state", this.s());

		if (cfg.rolesAll && cfg.rolesAll.length) {
			const missingRoles = cfg.rolesAll.filter((r) => !have.has(r));
			if (missingRoles.length > 0) {
				this.logger.warn(
					"AuthGuardService",
					"Access denied - Missing required roles",
					{
						missing: missingRoles,
						required: cfg.rolesAll,
					}
				);
				return false;
			}
		}

		if (cfg.rolesAny && cfg.rolesAny.length) {
			if (!cfg.rolesAny.some((r) => have.has(r))) {
				this.logger.warn(
					"AuthGuardService",
					"Access denied - None of the required roles present",
					{
						requiredAny: cfg.rolesAny,
						userRoles: Array.from(have),
					}
				);
				return false;
			}
		}

		this.logger.debug("AuthGuardService", "Access granted");
		return true;
	}
}
